import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Article as ArticleIcon } from '@mui/icons-material';

import PageHeader from '../../../components/common/PageHeader';
import SearchHeader from '../../../components/common/SearchHeader';
import PaginationComponent from '../../../components/common/PaginationComponent';
import FormBuilder from '../../../components/common/FormBuilder';
import DeleteConfirmDialog from '../../../components/common/DeleteConfirmDialog';
import QuotePackageTable from '../components/QuotePackageTable';
import QuotePackageSectionsDialog from '../components/QuotePackageSectionsDialog';
import quotePackageService from '../services/quotePackageService';
import { createQuotePackageSchema, updateQuotePackageSchema } from '../schemas/quotePackageFormSchema';

const ITEMS_PER_PAGE = 25;

const QuotePackageListingPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPackages, setTotalPackages] = useState(0);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [sectionsDialogOpen, setSectionsDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const loadPackages = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        skip: (currentPage - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        active_only: false,
      };

      const response = await quotePackageService.getAll(params);
      if (response.success) {
        const data = response.data || [];
        setPackages(data);
        setTotalPackages(response.total || data.length || 0);
      } else {
        setError(response.error || 'Σφάλμα φόρτωσης πακέτων');
        setPackages([]);
        setTotalPackages(0);
      }
    } catch (err) {
      setError('Σφάλμα φόρτωσης πακέτων');
      setPackages([]);
      setTotalPackages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredPackages = useMemo(() => {
    if (!searchTerm) return packages;
    const lower = searchTerm.toLowerCase();
    return packages.filter((pkg) => {
      return (
        pkg.name?.toLowerCase().includes(lower) ||
        pkg.description?.toLowerCase().includes(lower) ||
        pkg.package_type?.toLowerCase().includes(lower)
      );
    });
  }, [packages, searchTerm]);

  const handleCreateSubmit = async (formData) => {
    const payload = {
      name: formData.name,
      description: formData.description || null,
      package_type: formData.package_type || 'custom',
      is_active: formData.is_active,
    };
    const response = await quotePackageService.create(payload);
    if (response.success) {
      setCreateModalOpen(false);
      setNotification({ open: true, message: response.message, severity: 'success' });
      loadPackages();
      return { success: true };
    }
    return { success: false, error: response.error };
  };

  const handleEditSubmit = async (formData) => {
    if (!selectedPackage) return { success: false, error: 'Δεν βρέθηκε πακέτο' };
    const payload = {
      name: formData.name,
      description: formData.description || null,
      package_type: formData.package_type,
      is_active: formData.is_active,
    };
    const response = await quotePackageService.update(selectedPackage.id, payload);
    if (response.success) {
      setEditModalOpen(false);
      setNotification({ open: true, message: response.message, severity: 'success' });
      loadPackages();
      return { success: true };
    }
    return { success: false, error: response.error };
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPackage) return;
    const response = await quotePackageService.delete(selectedPackage.id);
    if (response.success) {
      setNotification({ open: true, message: response.message, severity: 'success' });
      loadPackages();
    } else {
      setNotification({ open: true, message: response.error, severity: 'error' });
    }
    setDeleteDialogOpen(false);
  };

  const handleDuplicate = async (pkg) => {
    const response = await quotePackageService.duplicate(pkg.id);
    if (response.success) {
      setNotification({ open: true, message: response.message, severity: 'success' });
      loadPackages();
    } else {
      setNotification({ open: true, message: response.error, severity: 'error' });
    }
  };

  const pageActions = [
    <Button
      key="create"
      variant="contained"
      startIcon={<AddIcon />}
      onClick={() => setCreateModalOpen(true)}
    >
      Νέο Πακέτο
    </Button>,
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Πακέτα Προσφορών"
        subtitle="Διαχειρίσου τα templates προσφορών και το περιεχόμενο τους"
        icon="📦"
        actions={pageActions}
      />

      <SearchHeader
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Αναζήτηση πακέτων (όνομα, περιγραφή, τύπος)..."
        showSearch
      />

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        </Box>
      )}

      <QuotePackageTable
        data={filteredPackages}
        loading={loading}
        onEdit={(pkg) => {
          setSelectedPackage(pkg);
          setEditModalOpen(true);
        }}
        onDelete={(pkg) => {
          setSelectedPackage(pkg);
          setDeleteDialogOpen(true);
        }}
        onDuplicate={handleDuplicate}
        onManageSections={(pkg) => {
          setSelectedPackage(pkg);
          setSectionsDialogOpen(true);
        }}
      />

      <PaginationComponent
        currentPage={currentPage}
        totalPages={Math.ceil(totalPackages / ITEMS_PER_PAGE)}
        totalItems={totalPackages}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={() => {}}
        disableItemsPerPage
      />

      <FormBuilder
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        schema={createQuotePackageSchema}
        onSubmit={handleCreateSubmit}
        modalProps={{
          title: 'Νέο Πακέτο Προσφοράς',
          maxWidth: 'sm',
        }}
      />

      <FormBuilder
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        schema={updateQuotePackageSchema}
        entity={selectedPackage}
        onSubmit={handleEditSubmit}
        modalProps={{
          title: 'Επεξεργασία Πακέτου Προσφοράς',
          maxWidth: 'sm',
        }}
      />

      <QuotePackageSectionsDialog
        open={sectionsDialogOpen}
        onClose={() => setSectionsDialogOpen(false)}
        quotePackage={selectedPackage}
        onChange={loadPackages}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Διαγραφή Πακέτου"
        message={
          <Typography>
            Θέλεις σίγουρα να διαγράψεις το πακέτο{' '}
            <strong>{selectedPackage?.name}</strong>; Η ενέργεια είναι μη αναστρέψιμη.
          </Typography>
        }
        confirmText="Διαγραφή"
        cancelText="Ακύρωση"
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuotePackageListingPage;

